/**
 * Minimal TAR (USTAR) stream writer for browser.
 * Produces a ReadableStream that emits TAR bytes as entries are pushed.
 * TAR format: each file = 512-byte header + data (padded to 512) + end blocks.
 * Partial files on truncation remain extractable (TAR is sequential).
 */

const BLOCK = 512;
const END_BLOCKS = new Uint8Array(BLOCK * 2); // two zero blocks at end

function pad(str: string, len: number): Uint8Array {
  let enc = new TextEncoder().encode(str);
  if (enc.length > len) enc = enc.slice(0, len);
  const out = new Uint8Array(len);
  out.set(enc);
  return out;
}

function octal(num: number, len: number): Uint8Array {
  return pad(num.toString(8).padStart(len - 1, '0') + ' ', len);
}

function createHeader(name: string, size: number): Uint8Array {
  const header = new Uint8Array(BLOCK);
  let offset = 0;

  // name (100) and prefix (155) - USTAR: full path = prefix + "/" + name
  let namePart = name;
  let prefixPart = '';
  if (name.length > 100) {
    const slash = name.lastIndexOf('/');
    if (slash >= 0) {
      prefixPart = name.slice(0, slash);
      namePart = name.slice(slash + 1);
    }
    if (prefixPart.length > 155) {
      const cutAt = prefixPart.lastIndexOf('/', 154);
      prefixPart = cutAt >= 0 ? prefixPart.slice(0, cutAt) : prefixPart.slice(0, 155);
    }
    if (namePart.length > 100) namePart = namePart.slice(-100);
  }
  header.set(pad(namePart, 100), offset);
  offset += 100;

  // mode (8) - 0644
  header.set(pad('0000644 ', 8), offset);
  offset += 8;

  // uid, gid (8 each)
  header.set(pad('0000000 ', 8), offset);
  offset += 8;
  header.set(pad('0000000 ', 8), offset);
  offset += 8;

  // size (12)
  header.set(octal(size, 12), offset);
  offset += 12;

  // mtime (12)
  header.set(octal(Math.floor(Date.now() / 1000), 12), offset);
  offset += 12;

  // chksum (8) - placeholder, fill later
  offset += 8;

  // typeflag (1) - '0' = regular file
  header[offset++] = 0x30;

  // linkname (100)
  offset += 100;

  // magic (6)
  header.set(new TextEncoder().encode('ustar\0'), offset);
  offset += 6;

  // version (2)
  header.set(new TextEncoder().encode('00'), offset);
  offset += 2;

  // uname, gname (32 each)
  offset += 64;

  // devmajor, devminor (8 each)
  offset += 16;

  // prefix (155)
  header.set(pad(prefixPart, 155), offset);
  offset += 155;

  // Checksum: sum of header bytes as unsigned, with chksum field as 8 spaces
  const chksumOffset = 148;
  header.set(pad('        ', 8), chksumOffset);
  let sum = 0;
  for (let i = 0; i < BLOCK; i++) sum += header[i];
  header.set(octal(sum, 8), chksumOffset);

  return header;
}

function getPadding(size: number): Uint8Array {
  const remainder = size % BLOCK;
  if (remainder === 0) return new Uint8Array(0);
  return new Uint8Array(BLOCK - remainder);
}

export interface TarEntry {
  name: string;
  data: Uint8Array;
}

/**
 * Creates a TAR stream. Call pushEntries() to add files; call close() when done.
 * The stream is consumed by piping to StreamSaver.
 */
export function createTarStream(): {
  pushEntries: (entries: TarEntry[]) => void;
  close: () => void;
  stream: ReadableStream<Uint8Array>;
} {
  const entryQueue: TarEntry[][] = [];
  let resolveWait: (() => void) | null = null;
  let closed = false;

  function wake(): void {
    if (resolveWait) {
      const r = resolveWait;
      resolveWait = null;
      r();
    }
  }

  const stream = new ReadableStream<Uint8Array>({
    async pull(controller) {
      while (true) {
        if (entryQueue.length > 0) {
          const batch = entryQueue.shift()!;
          for (const { name, data } of batch) {
            const header = createHeader(name, data.length);
            controller.enqueue(header);
            controller.enqueue(data);
            controller.enqueue(getPadding(data.length));
          }
          return;
        }
        if (closed) {
          controller.enqueue(END_BLOCKS);
          controller.close();
          return;
        }
        await new Promise<void>((r) => {
          resolveWait = r;
        });
      }
    },
  });

  return {
    pushEntries(entries: TarEntry[]) {
      if (entries.length === 0) return;
      entryQueue.push(entries);
      wake();
    },
    close() {
      closed = true;
      wake();
    },
    stream,
  };
}
